import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  agentHealthMonitorApi,
  type ListAlertsResponse,
} from '../services/agentHealthMonitorApi';

/**
 * Hooks React Query para o módulo `agent-health-monitor` (Opção B).
 *
 * Pattern: queries com staleTime 60s (não precisa polling agressivo —
 * o cron só roda 1x/dia), mutations invalidam cache das queries.
 */

const QUERY_KEY_BASE = ['agent-health-monitor'] as const;

/**
 * Lista alertas ativos de um agente específico. Chamado pela HealthTab
 * pra mostrar o banner proativo de drift de produção.
 *
 * StaleTime 60s — alertas mudam raramente (cron roda 1x/dia), não
 * precisa revalidar toda hora.
 */
export function useAgentDriftAlerts(agentId: string | undefined) {
  return useQuery<ListAlertsResponse, Error>({
    queryKey: [...QUERY_KEY_BASE, agentId, 'alerts'],
    queryFn: () => {
      if (!agentId) throw new Error('agentId é obrigatório');
      return agentHealthMonitorApi.listByAgent(agentId);
    },
    enabled: !!agentId,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Lista alertas ativos do tenant (todos os agentes). Usado pra badge
 * global no sino de notificações + dashboard admin futuro.
 */
export function useTenantDriftAlerts(enabled = true) {
  return useQuery<ListAlertsResponse, Error>({
    queryKey: [...QUERY_KEY_BASE, 'tenant', 'alerts'],
    queryFn: () => agentHealthMonitorApi.listActiveByTenant(),
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Mutation para reconhecer (acknowledge) um alerta. Invalida AMBOS os
 * caches (do agente específico + do tenant) pra refletir no banner +
 * badge do sino.
 */
export function useAcknowledgeDriftAlert() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { agentId: string; alertId: string }>({
    mutationFn: ({ agentId, alertId }) =>
      agentHealthMonitorApi.acknowledge(agentId, alertId),
    onSuccess: (_, { agentId }) => {
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEY_BASE, agentId, 'alerts'],
      });
      queryClient.invalidateQueries({
        queryKey: [...QUERY_KEY_BASE, 'tenant', 'alerts'],
      });
    },
  });
}
